/**
 * useActiveProjectResolver — picks the home page's active project.
 *
 * Resolution order:
 *   1. URL query param `?project=<shortId>` (via useShortIdFromLocation) when
 *      still plausible (unknown invalid only after the list has loaded).
 *   2. Project-list fetch failure → `'error'` (so callers can distinguish a
 *      real failure from an empty account, instead of silently falling
 *      through to `'empty'`)
 *   3. Project list still loading → `'loading'`
 *   4. **Multi-project** (`length ≥ 2`) on bare `/` with no valid URL pick →
 *      `'ready'` with **`shortId: null`** (home lobby — no implicit project).
 *   5. **Single project** → that project's `short_id`, `'ready'` (caller
 *      should redirect to `/projects/{short}/overview` per IP-260505).
 *   6. Empty (user has no projects) → `'empty'`.
 *
 * The resolver does NOT push the resolved shortId back into the URL.
 * `/` stays a stable bookmark. The project dropdown navigates to
 * `/projects/<shortId>/overview`. Optional `/?project=<shortId>` remains for
 * deep links until fully deprecated.
 *
 * Most-recent ordering is no longer used for bare `/` multi-project home;
 * backend `?sort=recent&limit=1` (D5 / issue #329) applies only where a single
 * implicit pick is still desired.
 */

import { useMemo } from 'react';

import { useShortIdFromLocation } from '@/hooks/v2/useShortIdFromLocation';
import { useProjects, type ProjectListItem } from '@/hooks/v2/useProjects';

export type ActiveProjectPhase = 'loading' | 'ready' | 'empty' | 'error';

export interface ActiveProjectResolution {
  shortId: string | null;
  phase: ActiveProjectPhase;
}

export function useActiveProjectResolver(): ActiveProjectResolution {
  const urlShortId = useShortIdFromLocation();
  const projects = useProjects();

  return useMemo<ActiveProjectResolution>(() => {
    if (projects.isError) return { shortId: null, phase: 'error' };
    // Optimistic: trust the URL shortId while projects haven't loaded so
    // path-based project routes don't wait on the home list. Once the list
    // arrives we validate — a stale `?project=ana0002` for an
    // archived/deleted project must not keep driving 404s.
    if (urlShortId) {
      const knownInvalid =
        projects.data !== undefined &&
        !projects.data.some((p) => p.short_id === urlShortId);
      if (!knownInvalid) return { shortId: urlShortId, phase: 'ready' };
    }
    if (projects.isLoading) return { shortId: null, phase: 'loading' };
    if (!projects.data || projects.data.length === 0) {
      return { shortId: null, phase: 'empty' };
    }
    const list = projects.data;
    if (list.length >= 2) {
      return { shortId: null, phase: 'ready' };
    }
    const only = list[0];
    return { shortId: only.short_id, phase: 'ready' };
  }, [urlShortId, projects.isError, projects.isLoading, projects.data]);
}
